// Adapted from Robbie Kohler's split-occasions work (41c2053 on feat/split-carryout-delivery-occasions).
import { formatOccasionName, OCCASION_DISPLAY_ORDER, OccasionType } from './types';

/**
 * Shape of an occasion as returned by the BFF (Byte Graph `occasions[]`).
 * Kept loose on purpose: the list view receives it untyped from the store context.
 */
export interface OccasionHoursLike {
  occasion: string;
  isEnabled?: boolean;
  hours?: Array<{
    timeFrame?: {
      startDate?: string | null;
      endDate?: string | null;
      intervalStartTime?: string;
      duration?: string | number;
      days?: number[];
    } | null;
  }>;
}

export interface TodayHoursLine {
  occasion: string;
  label: string;
  /** e.g. "11:00 AM - 11:00 PM" or "11:00 AM - 2:00 PM & 5:00 PM - 1:00 AM" */
  hours: string;
}

export type TodayHoursSummary =
  /** No occasion is enabled at all */
  | { kind: 'unavailable'; text: string }
  /** Occasions are enabled but none has regular hours today */
  | { kind: 'closed'; text: string }
  /** Every open occasion has identical hours today */
  | { kind: 'shared'; text: string }
  /** Occasions differ (e.g. Carryout closes 11 PM, Delivery closes 1 AM) */
  | { kind: 'split'; lines: TodayHoursLine[] };

const MINUTES_IN_HOUR = 60;
const HOURS_IN_DAY = 24;

/** ISO 8601 day of week: Monday = 1 ... Sunday = 7 (Byte Graph `timeFrame.days` uses this). */
export function isoDayOfWeek(date: Date = new Date()): number {
  const jsDay = date.getDay();
  return jsDay === 0 ? 7 : jsDay;
}

/** Parse "HH:MM" or "HH:MM:SS" (or a plain number of minutes) into minutes. */
function parseDurationMinutes(duration: string | number | undefined): number {
  if (typeof duration === 'string' && duration.includes(':')) {
    const [hours, minutes] = duration.split(':').map(Number);
    return (hours || 0) * MINUTES_IN_HOUR + (minutes || 0);
  }
  return Number(duration) || 0;
}

function formatClock(totalMinutes: number, use24Hour: boolean): string {
  const hour = Math.floor(totalMinutes / MINUTES_IN_HOUR) % HOURS_IN_DAY;
  const minute = totalMinutes % MINUTES_IN_HOUR;
  const paddedMinute = String(minute).padStart(2, '0');
  if (use24Hour) {
    return `${String(hour).padStart(2, '0')}:${paddedMinute}`;
  }
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${displayHour}:${paddedMinute} ${period}`;
}

/**
 * Today's regular hours for each enabled occasion, in display order.
 * Special (dated) time frames are ignored; split shifts are joined with " & ".
 * Occasions with no hours today are omitted.
 */
export function getTodayHoursByOccasion(
  occasions: OccasionHoursLike[] | undefined,
  today: number,
  use24Hour: boolean
): TodayHoursLine[] {
  const lines: TodayHoursLine[] = [];

  for (const occ of occasions || []) {
    if (!occ.isEnabled || !occ.hours?.length) continue;

    const slots: { start: number; text: string }[] = [];
    for (const hour of occ.hours) {
      const timeFrame = hour.timeFrame;
      if (!timeFrame?.days?.includes(today)) continue;
      // Dated frames are special hours (holidays, closures) — not the regular schedule
      if (timeFrame.startDate && timeFrame.endDate) continue;

      const [startHour, startMinute] = (timeFrame.intervalStartTime || '00:00')
        .split(':')
        .map(Number);
      const start = (startHour || 0) * MINUTES_IN_HOUR + (startMinute || 0);
      const end = start + parseDurationMinutes(timeFrame.duration);
      slots.push({
        start,
        text: `${formatClock(start, use24Hour)} - ${formatClock(end, use24Hour)}`,
      });
    }

    if (slots.length === 0) continue;
    slots.sort((a, b) => a.start - b.start);
    lines.push({
      occasion: occ.occasion,
      label: formatOccasionName(occ.occasion),
      hours: slots.map((s) => s.text).join(' & '),
    });
  }

  const orderIndex = (occasion: string) => {
    const idx = OCCASION_DISPLAY_ORDER.indexOf(occasion.toUpperCase() as OccasionType);
    return idx === -1 ? OCCASION_DISPLAY_ORDER.length : idx;
  };
  return lines.sort((a, b) => orderIndex(a.occasion) - orderIndex(b.occasion));
}

/**
 * Collapse today's per-occasion hours into what the store list should show:
 * one line when every occasion matches, one line per occasion when they differ.
 *
 * `visibleOccasions` limits the summary to occasions the manager can see and edit
 * (AVAILABLE_OCCASIONS). Hidden ones such as Dine In are still enabled in Byte Graph
 * with their own hours, and must not show up as "Today's Hours".
 */
export function summarizeTodayHours(
  occasions: OccasionHoursLike[] | undefined,
  today: number,
  use24Hour: boolean,
  visibleOccasions?: readonly string[]
): TodayHoursSummary {
  const visible = visibleOccasions?.map((o) => o.toUpperCase());
  const enabled = (occasions || []).filter(
    (occ) => occ.isEnabled && (!visible || visible.includes(occ.occasion.toUpperCase()))
  );
  if (enabled.length === 0) {
    return { kind: 'unavailable', text: 'No hours available' };
  }

  const lines = getTodayHoursByOccasion(enabled, today, use24Hour);
  if (lines.length === 0) {
    return { kind: 'closed', text: 'Closed today' };
  }

  const distinct = new Set(lines.map((line) => line.hours));
  if (distinct.size === 1) {
    return { kind: 'shared', text: lines[0].hours };
  }
  return { kind: 'split', lines };
}
